import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Col,
  Row,
  Input,
  Modal,
  Table,
  Button,
  Switch,
  Space,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryApi } from "../../store/actions/common";
import { triggerNotification } from "../../store/directActions";
import PageHeader from "../PageHeader";
import { onBarcodeScan } from "../../utils/barcodeEvents";
import useSocket from "../../hooks/useSocket";

// Dummy API functions - Replace these with actual API endpoints
const fetchScanMetaApi = async (scanCode) => {
  return queryApi(`ledger/scan_meta/${scanCode}`, "GET");
};

const addSlotsApi = async (data) => {
  return queryApi(`slot`, "POST", data);
};

function ModalSlots({
  showModal,
  toggleModal,
  onSuccess,
  checkDataBox = [],
  storeConfig,
  storeMeta,
}) {
  const [localData, setLocalData] = useState([]);
  const [scanMeta, setScanMeta] = useState(undefined);
  const [activatedCode, setActivatedCode] = useState("");
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const inputRef = useRef(null);
  const barcodeRef = useRef(null);
  const { sendMessage } = useSocket();

  const focusBarcode = () => {
    // Delay lets the modal finish its open animation before focusing.
    setTimeout(() => {
      barcodeRef.current?.focus({ cursor: "end" });
    }, 100);
  };

  useEffect(() => {
    if (showModal) {
      form.setFieldsValue({
        is_reverse: storeMeta?.scan_mode_reverse === 1,
        activated_code: "",
        slot_number: "",
      });
      setLocalData([]);
      setScanMeta(undefined);
      setActivatedCode("");
      focusBarcode();
    }
  }, [showModal, storeMeta, form]);

  // Mutation for fetching scan metadata
  const { mutate: fetchScanMeta, isPending: isScanning } = useMutation({
    mutationFn: (code) => fetchScanMetaApi(code),
    onSuccess: (scan_meta) => {
      console.log("scanned meta", scan_meta);
      form.setFieldsValue({ activated_code: scan_meta.scan_code });
      setActivatedCode(scan_meta.scan_code);

      if (scan_meta.is_inventory && !scan_meta.is_active) {
        if (!localData.some((item) => item.pack === scan_meta.pack)) {
          setScanMeta(scan_meta);
          if (inputRef.current) {
            inputRef.current.focus({
              cursor: "start",
            });
          }
        } else {
          triggerNotification({
            title: "Error",
            description: "You have already added this pack no",
            type: "error",
          });
        }
      } else {
        if (!scan_meta.is_inventory) {
          triggerNotification({
            title: "Error",
            description: "Please add this pack to inventory first",
            type: "error",
          });
        }
        if (scan_meta.is_active) {
          triggerNotification({
            title: "Error",
            description: "This pack is already active",
            type: "error",
          });
        }
      }
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
    },
  });

  // Mutation for adding slots
  const { mutate: addSlots, isPending: isAdding } = useMutation({
    mutationFn: (data) => addSlotsApi(data),
    onSuccess: () => {
      setLocalData([]);
      toggleModal(false);
      queryClient.invalidateQueries({
        queryKey: ["slot"],
      });
      if (onSuccess) {
        onSuccess();
      }
      triggerNotification({
        title: "Success",
        description: "Slots activated successfully",
        type: "success",
      });
      sendMessage("screen-refresh");
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to activate slots",
        type: "error",
      });
    },
  });

  const getSerchedData = (data) => {
    if (data && data.trim()) {
      fetchScanMeta(data);
    }
  };

  const onFormSubmit = (values) => {
    console.log("form data", values, scanMeta);
    if (
      storeConfig?.barcode_len &&
      ("" + (values.activated_code ?? "")).length !== storeConfig.barcode_len
    ) {
      triggerNotification({
        title: "Error",
        description: `Barcode length should be equal to ${storeConfig.barcode_len}`,
        type: "error",
      });
      return;
    }

    setScanMeta(undefined);
    form.resetFields();
    form.setFieldsValue({ is_reverse: storeMeta?.scan_mode_reverse === 1 });
    setActivatedCode("");
    focusBarcode();

    if (
      checkDataBox.some(
        (item) => item.slot_number == values.slot_number && !item.is_paused,
      )
    ) {
      triggerNotification({
        title: "Error",
        description: "This box is already active!",
        type: "error",
      });
      return;
    }

    if (
      !localData.some(
        (item) => item.slot_number === values.slot_number && !item.is_paused,
      )
    ) {
      setLocalData([{ ...scanMeta, ...values }, ...localData]);
    } else {
      triggerNotification({
        title: "Error",
        description: "You have already added this box no",
        type: "error",
      });
    }
  };

  const onBulkUpdateSlot = () => {
    addSlots(
      localData.map((item) => {
        return {
          activated_code: item.scan_code,
          slot_number: item.slot_number,
          is_reverse: item.is_reverse,
          is_closed: false,
        };
      }),
    );
  };

  const inventoryColumns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => (
        <div>
          {record.name} -- {record.game_number}
        </div>
      ),
      width: "200px",
    },

    {
      title: <div style={{ minWidth: "max-content" }}>Pack #</div>,
      dataIndex: "pack",
      key: "pack",
      render: (item, record, index) => (
        <div>
          {record.pack}
          {/* <br></br>
          {record.pack_size} */}
        </div>
      ),
      width: 100,
    },
    {
      title: "Reversed",
      dataIndex: "is_reverse",
      key: "is_reverse",
      render: (is_reverse) => <div>{is_reverse ? "Reversed" : "Normal"}</div>,
    },

    // {
    //   title: "Vallue",
    //   dataIndex: "amount",
    //   key: "amount",
    //   render: (item, record, index) => (
    //     <div>
    //       {record.currency}
    //       {record.amount}
    //     </div>
    //   ),
    // },
    {
      title: "Value",
      dataIndex: "totalPack",
      key: "totalPack",
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount * record.pack_size}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (item, record, index) => (
        <Space size="middle">
          <DeleteOutlined
            style={{ fontSize: 22, color: "red" }}
            onClick={() => {
              setLocalData(
                localData.filter((item, itemIndex) => itemIndex !== index),
              );
            }}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    return onBarcodeScan(async (barcode) => {
      if (showModal) getSerchedData(barcode);
    });
  }, [showModal]);

  return (
    <Modal
      title="Activate"
      open={showModal}
      onOk={() => onBulkUpdateSlot()}
      onCancel={() => {
        setLocalData([]);
        toggleModal(false);
      }}
      okButtonProps={{
        disabled: localData?.length === 0,
        loading: isAdding,
      }}
      cancelButtonProps={{ disabled: isAdding }}
      width="80%"
      maskClosable={false}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form
            form={form}
            onFinish={onFormSubmit}
            layout="vertical"
            hideRequiredMark
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="activated_code"
                  label="Scan Barcode"
                  rules={[
                    { required: true, message: "Please enter scan barcode" },
                  ]}
                  style={{ margin: 0 }}
                >
                  <Input
                    ref={barcodeRef}
                    placeholder="eg. Scan the barcode"
                    value={activatedCode}
                    onChange={(e) => {
                      setActivatedCode(e.target.value);
                      if (
                        storeConfig &&
                        e.target.value?.length === storeConfig?.barcode_len
                      )
                        getSerchedData(e.target.value);
                    }}
                    disabled={isScanning}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="slot_number"
                  label="Box #"
                  rules={[{ required: true, message: "Please enter Box no" }]}
                  style={{ margin: 0 }}
                >
                  <Input
                    placeholder="eg. any numeric value"
                    ref={inputRef}
                    onChange={(e) => {
                      const newNumber = parseInt(e.target.value || "0", 10);
                      if (Number.isNaN(newNumber) || newNumber > 999) {
                        form.setFieldsValue({
                          ...form.getFieldsValue(),
                          slot_number: "",
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="is_reverse"
                  label="Reverse Mode"
                  style={{ margin: 0 }}
                  valuePropName="checked"
                >
                  <Switch disabled />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Button
                  style={{ marginTop: 30, display: "none" }}
                  type="primary"
                  htmlType="submit"
                />
              </Col>
            </Row>
          </Form>
        </Col>
        <Col span={24}>
          <PageHeader
            title={`${"$"}${localData.reduce(
              (value, item) => value + item.amount * item.pack_size,
              0,
            )}`}
            subTitle={`Total pack : ${localData.length}`}
            extra={[
              <Button
                key="3"
                onClick={() => {
                  setLocalData([]);
                }}
                disabled={isAdding}
              >
                Clear
              </Button>,
            ]}
            wrapperClasses="mt-4"
          />
        </Col>
        <Col span={24}>
          <div style={{ overflowY: "auto" }}>
            <Table
              scroll={{ y: 220 }}
              dataSource={localData}
              bordered
              columns={inventoryColumns}
              pagination={false}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default ModalSlots;
